import React, { useEffect, useRef } from 'react'
import { useUserInfo } from 'src/services/use-user-info'
import { history } from 'src/app'
import {
  initTracker,
  trackPageView,
  trackSessionStart,
  destroyTracker,
} from './tracker'
import { AppId } from './types'

interface TrackingProviderProps {
  appId: AppId
  children: React.ReactNode
}

export const TrackingProvider: React.FC<TrackingProviderProps> = ({ appId, children }) => {
  const data = useUserInfo()
  const user = data?.me
  const initializedRef = useRef(false)

  // Initialize tracker once user is available and on a workspace page
  useEffect(() => {
    if (!user || initializedRef.current) return

    const pathname = window.location.pathname
    if (!pathname.startsWith('/workspace')) return

    initializedRef.current = true

    initTracker(appId, {
      id: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      organizationId: user.account?.id?.toString(),
      organizationName: user.account?.name,
      role: user.role,
    })

    trackSessionStart()
    trackPageView(pathname)

    return () => {
      destroyTracker()
      initializedRef.current = false
    }
  }, [user, appId])

  // Track route changes via history.listen()
  useEffect(() => {
    const unlisten = history.listen((location) => {
      if (!location.pathname.startsWith('/workspace')) return

      // Lazy init: if tracker hasn't started yet, start it on first workspace page
      if (!initializedRef.current && user) {
        initializedRef.current = true
        initTracker(appId, {
          id: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          organizationId: user.account?.id?.toString(),
          organizationName: user.account?.name,
          role: user.role,
        })
        trackSessionStart()
      }

      trackPageView(location.pathname)
    })

    return unlisten
  }, [user, appId])

  return <>{children}</>
}
